/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import React, { Component } from 'react';
import { SVGIcon, Multilinetruncatedtext, TruncatedText, Label } from '@ba/ba-ui-carbon-toolkit';
import PropTypes from 'util/PropTypes';
import { A11YCollections, A11YDirection } from 'lib/@waca/a11y/src/js';
import classnames from 'classnames';
import _ from 'underscore';
import { ClickableTile } from '@ba/ba-ui-carbon';
import $ from 'jquery';
import '../stylesheets/visTypePicker.scss';
import { VisTypeModel } from './VisTypeModel';

/**
 * The VisTypePicker component is responsible for requesting/rendering a selection of visTypes grouped by 'usage' (eg: Trend/Comparison)
 * Its
 */
class VisTypePicker extends Component {
	constructor(props) {
		super(props);

		//An ordered list of visualization "UsageIds"
		//A usage for a visualization describes what the visualization is used for.
		this.visUsageDisplayOrder = [
			'Comparison',
			'PartsToWhole',
			'Trend',
			'Relationships',
			'TablesAndSummary',
			'Advanced',
			'SocialInsights',
			'Geospatial',
			'Other'];

		this.a11y = new A11YCollections({
			direction: [A11YDirection.UP_DOWN, A11YDirection.LEFT_RIGHT],
			handlers: {
				elements: (element) => this._getChartItems(element)
			}
		});
		// allow only 1 tap event at a time from mobile browsers
		this.handleSelectVisType = _.debounce(this.handleSelectVisType, 2000, true);
	}

	/**
	 * Render all usages in the visUsageDisplayOrder
	 */
	_displayChartTypesByUsage() {
		return this.visUsageDisplayOrder.map((visUsageId, idx) => {
			const visUsage = this.props.model.getChartSpecs(visUsageId);
			if (visUsage) {
				return (
					<div className="vis-type-picker__usage"
						key={`usage${idx}`}
						data-usage-id={visUsageId}
						onKeyPress={ (event) => this.handleKeyPress(event) }
						onKeyDown={ (event) => this.handleKeyDown(event) }>
						<Label className="vis-type-picker__usage__label" label={visUsage.usageLabel} />
						<div className="vis-type-picker__usage__list">
							{this._addChartListToUsage(visUsage)}
						</div>
					</div>
				);
			}
		});
	}

	/**
	 * process a single usage by rendering the icons and localized name for each chart in the usage.
	 * @param visUsage - the usage entry which contains the id and list of charts for that usage (as returned by the visDefinition service API)
	 */
	_addChartListToUsage(visUsage) {

		return visUsage.chartTypes
			.map((chartType, index) => {
				if (this.props.isExtVis && this.props.isExtVis(chartType.id)) {
					return null;
				}
				const chartTypeLabel = chartType.name;
				const visTypeTileClasses = classnames({
					'vis-type-tile': true,
					'selected': this._isSelected(chartType)
				});
				const iconName = chartType.icon || 'customVisual';
				const icon = this.props.iconsFeature.getIcon(iconName) || {};
				const iconID = icon.id || this.props.iconsFeature.getIcon('customVisual').id; //customVisual is the default fall back
				const SEPARATOR = ' ';
				const containSeparator = chartTypeLabel && chartTypeLabel.includes(SEPARATOR);
				return (
					<ClickableTile
						className={visTypeTileClasses}
						key={index}
					>
						<div className='vis-type-picker__usage__list__item'
							data-vis-id={chartType.id}
							tabIndex={0}
							onClick={(event) => this.handleSelectVisType(chartType.id, event)}>
							<SVGIcon
								className='vis-type-picker__usage__list__item__icon'
								iconId={ iconID }
								height={24}
								width={24}>
							</SVGIcon>
							<SVGIcon
								className='checkMarkIcon'
								iconId={this.props.iconsFeature.getIcon('CheckmarkSVG').id}
								height={14}
								width={14}>
							</SVGIcon>
							{/* The width of item title is calculated as 90 (list item width) - 8 * 2 (two paddings) = 74px, since <TruncatedText> is not clickable by nature, we prevent cursor to change on hover */}
							<div className='vis-type-picker__usage__list__item__title'>
								{containSeparator ?
									(
										<Multilinetruncatedtext
											className='vis-type-picker__usage__list__item__title__label'
											value={chartTypeLabel}
											numLines={2}
										/>
									) : (
										<TruncatedText
											className='vis-type-picker__usage__list__item__title__label'
											value={chartTypeLabel}
										/>
									)
								}
							</div>
						</div>
					</ClickableTile>
				);
			});
	}

	/*Select the chart item if it matches the passed in SelectedVisID prop*/
	_isSelected(chartType){
		return chartType && this.props.selectedVisId === chartType.id;
	}

	/**
	 * render the visTypePicker control.
	 * The render could occur inside a dialog or other div.
	 */
	render() {
		const usageJSX = this._displayChartTypesByUsage();
		return (
			<div className="vis-type-picker" tabIndex="-1">
				{ usageJSX }
			</div>
		);
	}

	handleKeyPress(event) {
		if (event.key === 'Enter') {
			this.handleSelectVisType(this.focusedChartId());
		}
	}

	handleKeyDown(event) {
		this.a11y.onKeyDown(event);
	}

	handleSelectVisType(id, event = {}) {
		if ((!event.detail || event.detail === 1) && id) { // prevent superfluous click
			this.props.onVisTypeSelected(id);
		}
	}

	focusedChartId() {
		const id = document.activeElement.dataset.visId;

		this.handleSelectVisType(id);
	}

	_getChartItems(element) {
		return $(element)
			.closest('.vis-type-picker__usage')
			.find('.vis-type-picker__usage__list__item');
	}
}

VisTypePicker.propTypes = {
	model: PropTypes.stateTree(VisTypeModel).isRequired,
	onVisTypeSelected: PropTypes.func,
	isVizTypeLocked: PropTypes.bool, /*True means vis-picker is in auto recommend mode*/
	selectedVisId: PropTypes.string,
	isExtVis: PropTypes.func,
	iconsFeature: PropTypes.object.isRequired
};

export default VisTypePicker;



// WEBPACK FOOTER //
// ./src/js/lib/@waca/vis-type-picker/src/js/VisTypePicker.js